import { ArrowUp, Play, Sunrise } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { localDay } from '../../shared/calendar';
import { focusChanges, taskFocus } from '../../shared/focus';
import type { LocalRecord, Task } from '../../shared/model';
import { taskSuggestions } from '../../shared/productivity';
import { returnSummary } from '../../shared/returning';

import { dayLine, numberWord, workspaceHour } from '../lib/day-words';
import { takeSetOutRequest } from '../lib/desktop';
import { workspace } from '../lib/engine';
import { placedHere, setTaskDone } from '../lib/placements';
import { goTo, notePath, RouteLink, taskPath } from '../lib/routes';
import { taskStone } from '../lib/stones';
import { DailyLoad } from './DailyLoad';
import { focusRuntime } from './FocusHistory';
import { FocusPanel } from './FocusPanel';
import { LearningMorning } from './LearningPage';
import { TaskContext } from './Productivity';
import { SetOutPrompt, SetOutSheet } from './SetOut';
import { TaskBadges, taskReason, taskStatus } from './StatusBadge';
import { CairnStack, PebbleButton, StoneMark } from './Stones';
import { TrailScene, useSky } from './TrailScene';
import { Button, ButtonLink, Chip, IconButton, InlineCreate } from './ui';
import { WelcomeBack } from './WelcomeBack';

const word = (n: number) => {
  const text = numberWord(n);

  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Where the day is, in two short lines — the phone's greeting. Never a score
 * and never a scolding: what is placed, what is left, and a finished cairn
 * that says stop rather than more.
 */
export function greeting(placed: number, open: number, total: number): [string, string] {
  if (total === 0) {
    return ['Choose two or', 'three stones.'];
  }

  if (open === 0) {
    return ['Cairn complete.', 'That’s the day.'];
  }

  const left = total > 5 ? 'A tall stack today.' : `${word(open)} to go.`;

  if (placed === 0) {
    return ['A fresh cairn.', total > 5 ? left : `${word(open)} to place.`];
  }

  return [`${word(placed)} ${placed === 1 ? 'stone' : 'stones'} placed.`, left];
}

/** A day remembered on this device, like a dismissed banner; never synced. */
function useDayMark(key: string) {
  const [value, setValue] = useState(() => {
    try {
      return localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  });

  function mark(day: string) {
    setValue(day);

    try {
      localStorage.setItem(key, day);
    } catch {
      // A private window may refuse storage; the choice holds for this visit.
    }
  }

  return [value, mark] as const;
}

/** The hour in the workspace's zone, refreshed every five minutes: enough for a sky. */
function useHour() {
  const [hour, setHour] = useState(() => workspaceHour());

  useEffect(() => {
    const timer = setInterval(() => setHour(workspaceHour()), 300_000);

    return () => clearInterval(timer);
  }, []);

  return hour;
}

/**
 * The top of Today: a landscape with the day's cairn standing in it, the date
 * and a line saying where the day is. The sky follows the workspace's clock
 * and is always night in the dark theme.
 */
function TodayHeader({
  day,
  placed,
  open,
  welcome,
  onSetOut,
}: {
  day: string;
  placed: LocalRecord[];
  open: LocalRecord[];
  welcome: boolean;
  onSetOut: () => void;
}) {
  const sky = useSky(useHour());
  const total = placed.length + open.length;

  const [first, second] = welcome
    ? ['Welcome back.', 'The trail waited.']
    : greeting(placed.length, open.length, total);

  const label =
    total === 0
      ? 'Today’s cairn is empty. Choose two or three things to do.'
      : placed.length === total
        ? `Today’s cairn is complete: ${total} of ${total} stones placed.`
        : `Today’s cairn: ${placed.length} of ${total} stones placed.`;

  return (
    <header className="today-header">
      <div className="today-titles">
        <h1>Today</h1>
        <p className="today-date">{dayLine(day)}</p>
        <p className="today-greeting" aria-live="polite">
          {first}
          <br />
          {second}
        </p>
      </div>
      <IconButton
        className="today-set-out"
        variant="secondary"
        label="Set out: choose today’s stones and reserve time for them"
        title="Set out"
        onClick={onSetOut}
      >
        <Sunrise aria-hidden="true" />
      </IconButton>
      <div role="img" aria-label={label}>
        <TrailScene sky={sky} height={220} flower={welcome}>
          <CairnStack
            placed={placed.map(r => taskStone(r.id, r.value as Task))}
            planned={open.map(r => taskStone(r.id, r.value as Task).silhouette)}
            capstone={total > 0 && open.length === 0}
          />
        </TrailScene>
      </div>
    </header>
  );
}

/**
 * One committed task: its pebble, its title and status and nothing else. The
 * pebble finishes the task, as Complete does on the session panel, and its
 * stone drops onto the cairn at the top of the page.
 */
function CommitmentCard({
  record,
  records,
  deviceId,
  selected,
  onStart,
}: {
  record: LocalRecord;
  records: LocalRecord[];
  deviceId: string;
  selected: boolean;
  onStart: () => void;
}) {
  const task = record.value as Task;
  const tone = taskStatus(task, record.id, records).tone;

  return (
    <article
      className={`commitment-card ${selected ? 'selected' : ''} ${task.done ? 'done' : ''}`}
      data-tone={tone}
    >
      <PebbleButton
        stone={taskStone(record.id, task)}
        done={task.done}
        dropped={task.dropped}
        title={task.title}
        onToggle={() => void setTaskDone(record, !task.done, deviceId).catch(() => {})}
      />
      <RouteLink className="commitment-text" href={taskPath(record.id)}>
        <strong>{task.title}</strong>
        <TaskBadges record={record} records={records} />
      </RouteLink>
      {!task.done && tone !== 'focus' && (
        <IconButton
          variant="secondary"
          label={`${task.focus?.sessionOpen || tone === 'attention' ? 'Resume' : 'Start'} “${task.title}”`}
          onClick={onStart}
        >
          <Play aria-hidden="true" />
        </IconButton>
      )}
    </article>
  );
}

/** One suggestion, with its stone and the reason it is being suggested. */
function UpNextRow({
  record,
  records,
  day,
  onCommit,
}: {
  record: LocalRecord;
  records: LocalRecord[];
  day: string;
  onCommit: () => void;
}) {
  const task = record.value as Task;
  const stone = taskStone(record.id, task);

  return (
    <article className="up-next-row">
      <span className="up-next-stone">
        <StoneMark mineral={stone.mineral} silhouette={stone.silhouette} />
      </span>
      <RouteLink className="up-next-text" href={taskPath(record.id)}>
        <strong>{task.title}</strong>
        <small>{taskReason(task, record.id, records, day).label}</small>
      </RouteLink>
      <IconButton variant="secondary" label={`Commit “${task.title}” to today`} onClick={onCommit}>
        <ArrowUp aria-hidden="true" />
      </IconButton>
    </article>
  );
}

/** "Stone placed", with a way back, for a few seconds after this browser places one. */
function PlacedToast({ record, onUndo }: { record: LocalRecord; onUndo: () => void }) {
  const task = record.value as Task;
  const stone = taskStone(record.id, task);

  return (
    <div className="placed-toast" role="status">
      <span className="placed-toast-stone">
        <StoneMark mineral={stone.mineral} silhouette={stone.silhouette} />
      </span>
      <span className="placed-toast-text">
        <strong>Stone placed</strong>
        <small>{task.title}</small>
      </span>
      <Button variant="ghost" aria-label={`Undo, reopen “${task.title}”`} onClick={onUndo}>
        Undo
      </Button>
    </div>
  );
}

/**
 * Today is a console, and the day is also a cairn. A landscape heads the page
 * with the day's stones stacked in it: one for every commitment finished, a
 * dashed outline for each still to do, and a capstone once they are all done.
 * None of it counts days or keeps a streak; the cairn is today's.
 */
export function TodayPage({
  records,
  notes,
  priorities,
  currentTask,
  active,
  deviceId,
  name,
  capture,
  onCapture,
  onCaptureSubmit,
  onSelect,
  onSaveTask,
  onSwitch,
}: {
  records: LocalRecord[];
  notes: LocalRecord[];
  priorities: LocalRecord[];
  currentTask?: LocalRecord;
  active?: LocalRecord;
  deviceId: string;
  /** The owner's name, for Set out's greeting. */
  name: string;
  capture: string;
  onCapture: (value: string) => void;
  onCaptureSubmit: () => void;
  onSelect: (id: string) => void;
  onSaveTask: (record: LocalRecord, value: Task) => void;
  /** Another task is running: ask for its stopping point before switching. */
  onSwitch: (record: LocalRecord) => void;
}) {
  const day = localDay();
  const tasks = records.filter(r => r.kind === 'task' && !(r.value as Task).trashed);

  // Running work first, finished work last, titles between — as on the phone.
  const rank = (record: LocalRecord) => {
    const task = record.value as Task;

    return task.done ? 2 : taskStatus(task, record.id, records).tone === 'focus' ? 0 : 1;
  };

  const commitments = tasks
    .filter(r => {
      const task = r.value as Task;

      return task.today && !task.dropped && (!task.done || task.completedOn === day);
    })
    .sort(
      (a, b) => rank(a) - rank(b) || (a.value as Task).title.localeCompare((b.value as Task).title),
    );

  const open = commitments.filter(r => !(r.value as Task).done);

  // The stones in the order they were finished, base first.
  const placed = commitments
    .filter(r => (r.value as Task).done)
    .sort(
      (a, b) =>
        a.updatedAt.localeCompare(b.updatedAt) ||
        (a.value as Task).title.localeCompare((b.value as Task).title),
    );

  const upNext = taskSuggestions(records)
    .filter(r => !commitments.some(c => c.id === r.id))
    .slice(0, 5)
    .map(r => tasks.find(t => t.id === r.id))
    .filter((r): r is LocalRecord => Boolean(r));

  const returning = useMemo(() => returnSummary(records), [records]);
  const [welcomedOn, setWelcomedOn] = useDayMark('cairn.welcomedOn');
  const [setOutOn, setSetOutOn] = useDayMark('cairn.setOutOn');
  const [settingOut, setSettingOut] = useState(false);

  // The Mac app's File → Set Out and its palette ask from outside the page.
  useEffect(() => {
    const take = () => {
      if (takeSetOutRequest()) {
        setSettingOut(true);
      }
    };

    take();
    window.addEventListener('cairn:set-out', take);

    return () => window.removeEventListener('cairn:set-out', take);
  }, []);

  // Welcome back shows on the first day after a gap until it is put away, and
  // never over work already under way. The morning invitation gives way to it
  // and to a day already begun.
  const showsWelcome = Boolean(returning) && welcomedOn !== day && !active;
  const showsSetOut = !showsWelcome && setOutOn !== day && placed.length === 0 && !active;

  // A stone that arrives on the cairn: if this browser placed it, offer it back.
  const [toast, setToast] = useState<string>();
  const seen = useRef(new Set(placed.map(r => r.id)));
  const placedKey = placed.map(r => r.id).join();

  useEffect(() => {
    const now = new Set(placedKey ? placedKey.split(',') : []);
    const fresh = [...now].find(id => !seen.current.has(id) && placedHere(id));

    seen.current = now;

    if (!fresh) {
      return;
    }

    setToast(fresh);

    const timer = setTimeout(() => setToast(undefined), 4500);

    return () => clearTimeout(timer);
  }, [placedKey]);

  const toastRecord = toast ? tasks.find(r => r.id === toast) : undefined;
  const task = currentTask?.value as Task | undefined;
  const workingNote = notes.find(r => r.id === task?.noteId);

  function startTask(record: LocalRecord) {
    if (active && active.id !== record.id) {
      onSwitch(record);

      return;
    }

    onSelect(record.id);
    const snapshot = workspace.snapshot();

    void workspace
      .saveMany(
        focusChanges(
          snapshot.records,
          record.id,
          taskFocus(snapshot.records, record.id, deviceId).deviceId,
          focusRuntime,
          'start',
          '',
          Date.now(),
          !navigator.onLine || snapshot.offline,
        ),
      )
      .catch(() => {});
  }

  function openSetOut() {
    setSettingOut(true);
  }

  return (
    <main className="today-page">
      <TodayHeader
        day={day}
        placed={placed}
        open={open}
        welcome={showsWelcome}
        onSetOut={openSetOut}
      />
      <div className="today-grid">
        <div className="today-main">
          {showsWelcome && returning ? (
            <WelcomeBack
              summary={returning}
              records={records}
              day={day}
              onOpen={id => {
                // Only opens the task: picking up never starts the clock.
                setWelcomedOn(day);
                goTo(taskPath(id));
              }}
              onSetOut={openSetOut}
              onDismiss={() => setWelcomedOn(day)}
            />
          ) : (
            <>
              {showsSetOut && <SetOutPrompt onOpen={openSetOut} onSkip={() => setSetOutOn(day)} />}
              {currentTask && task && (
                <section className="today-now" aria-label="Current task">
                  <FocusPanel
                    wide
                    key={`focus:${currentTask.id}`}
                    record={currentTask}
                    records={records}
                    deviceId={deviceId}
                    notes={notes}
                    active={active}
                    save={value => onSaveTask(currentTask, value)}
                    startOther={() => onSwitch(currentTask)}
                    context={
                      <TaskContext
                        key={`context:${currentTask.id}`}
                        record={currentTask}
                        records={records}
                      />
                    }
                  />
                  <div className="today-now-links">
                    {workingNote && (
                      <ButtonLink variant="ghost" href={notePath(workingNote.id)}>
                        Open working note
                      </ButtonLink>
                    )}
                    <ButtonLink variant="ghost" href={taskPath(currentTask.id)}>
                      Open full task
                    </ButtonLink>
                    <Button
                      variant="ghost"
                      onClick={() => onSaveTask(currentTask, { ...task, today: !task.today })}
                    >
                      {task.today ? 'Move back to Inbox' : 'Choose for Today'}
                    </Button>
                  </div>
                </section>
              )}
            </>
          )}
          <section className="today-stones" aria-label="Today’s stones">
            {commitments.length ? (
              <>
                <div className="section-heading today-section-heading">
                  <h2>Today’s stones</h2>
                  <span className="muted">
                    {placed.length} of {commitments.length} placed
                  </span>
                </div>
                <div className="commitment-list">
                  {commitments.map(record => (
                    <CommitmentCard
                      key={record.id}
                      record={record}
                      records={records}
                      deviceId={deviceId}
                      selected={currentTask?.id === record.id}
                      onStart={() => startTask(record)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="empty-state compact">
                <Sunrise aria-hidden="true" />
                <div>
                  <h2>Start with one thing</h2>
                  <p>Choose from Up next, or capture a task.</p>
                  <Chip onClick={openSetOut}>
                    <Sunrise aria-hidden="true" />
                    Set out
                  </Chip>
                </div>
              </div>
            )}
          </section>
        </div>
        <aside className="today-rail">
          <section className="today-section">
            <div className="section-heading today-section-heading">
              <h2>Capture</h2>
            </div>
            <div className="detail-card today-capture">
              <InlineCreate
                label="What needs doing?"
                placeholder="What needs doing?"
                value={capture}
                onChange={onCapture}
                action="Capture to Inbox"
                icon="add"
                onSubmit={e => {
                  e.preventDefault();
                  onCaptureSubmit();
                }}
              />
              <span className="small-muted">Goes to Inbox. Only a title is needed.</span>
            </div>
          </section>
          <section className="today-section">
            <div className="section-heading today-section-heading">
              <h2>Up next</h2>
            </div>
            {upNext.length ? (
              <div className="up-next-list">
                {upNext.map(record => (
                  <UpNextRow
                    key={record.id}
                    record={record}
                    records={records}
                    day={day}
                    onCommit={() => {
                      const stored = tasks.find(t => t.id === record.id);

                      if (stored) {
                        onSaveTask(stored, { ...(stored.value as Task), today: true });
                      }
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state compact">
                <ArrowUp aria-hidden="true" />
                <div>
                  <h2>Nothing waiting</h2>
                  <p>Capture a thought when it comes. For now, enjoy the quiet.</p>
                </div>
              </div>
            )}
          </section>
          <LearningMorning records={records} />
          <DailyLoad records={records} priorities={priorities} />
        </aside>
      </div>
      {toastRecord && (
        <PlacedToast
          key={toastRecord.id}
          record={toastRecord}
          onUndo={() => {
            setToast(undefined);
            void setTaskDone(toastRecord, false, deviceId).catch(() => {});
          }}
        />
      )}
      {settingOut && (
        <SetOutSheet
          records={records}
          name={name}
          day={day}
          carried={open}
          suggested={upNext}
          placed={placed}
          returning={returning}
          onClose={() => setSettingOut(false)}
          onFinish={() => {
            setSetOutOn(day);
            setWelcomedOn(day);
          }}
        />
      )}
    </main>
  );
}
